/* eslint-disable react-hooks/rules-of-hooks */
import React, { FC, useCallback, useMemo } from 'react'
import { Typography } from '@mui/material'
import { MOBILITY_LINE_COLORS } from 'src/components/theme/colors'
import { ShortDot } from 'src/components/chart-legend/icons/shortdot'
import { Dash } from 'src/components/chart-legend/icons/dash'
import { DashDot } from 'src/components/chart-legend/icons/dashdot'
import { Solid } from 'src/components/chart-legend/icons/solid'
import { LongDashDot } from 'src/components/chart-legend/icons/long-dash-dot'
import { Square } from '@emotion-icons/fluentui-system-filled/Square'
import { fullAddress } from 'src/utils/full-address'
import styled from '@emotion/styled'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { rgbaStrOpacity } from 'src/utils/colors'
import { AMLocation } from 'src/common-services'

export const ChartTooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 140px;
  border-radius: ${Shapes.borderRadius}px;
  background-color: white;
  box-shadow: ${Colors.BOX_SHADOW};
  z-index: 999999;
  overflow: hidden;
`
export const ChartTooltipHeader = styled.div`
  display: flex;
  gap: ${Shapes.spacing[1]}px;
  padding: ${Shapes.spacing[2]}px;
  background-color: ${Colors.bgDark};
  & > p:first-of-type {
    ${useTypography(Typographies.text1m)}
  }
  & > p:last-of-type {
    ${useTypography(Typographies.text1)}
  }
  border-bottom: 1px solid ${Colors.border};
`
export const ChartTooltipContent = styled.div`
  display: flex;
  ${useTypography(Typographies.text1m)}
`

const LocationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${Colors.border};
  :nth-of-type(4) {
    border-bottom: none;
  }
`
const LocationHeaderWrapper = styled.div`
  display: flex;
  gap: ${Shapes.spacing[1]}px;
`
const LocationHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[1]}px;
  padding: ${Shapes.spacing[2]}px;
  p {
    max-width: 240px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  p:first-of-type {
    ${useTypography(Typographies.textmd)};
  }
  p:last-of-type {
    ${useTypography(Typographies.text1m)}
  }
`
const LocationContent = styled.div`
  display: flex;
  flex-direction: column;
`

const LocationsColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  :last-of-type {
    border-left: 1px solid ${Colors.border};
    border-bottom: 'none';
  }
`

const ProviderVisitsRow = styled.div<{ color?: string }>`
  display: flex;
  background-color: ${(props) => (props.color ? rgbaStrOpacity(props.color, 0.1) : 'transparent')};
  gap: ${Shapes.spacing[2]}px;
  align-items: center;
  ${useTypography(Typographies.text1m)}
  padding: ${Shapes.spacing[1] / 2}px ${Shapes.spacing[2]}px;
  min-width: 200px;
`
const ProviderLegendColumn = styled.div`
  flex: 1;
  display: flex;
  gap: ${Shapes.spacing[2]}px;
  align-items: center;
  p {
    ${useTypography(Typographies.text1m)}
  }
`
const ProviderVisitsColumn = styled.div`
  p {
    ${useTypography(Typographies.text1m)}
  }
`

const BasicStatTexts = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Shapes.spacing[2]}px;
  gap: 8px;
`

export type ChartTooltipLegendIcon = 'shortdot' | 'dash' | 'dashdot' | 'solid' | 'square' | 'long-dash-dot'

export type ChartToolTipItemStat = {
  color: string
  label: string
  value: string | number
  icon: ChartTooltipLegendIcon
}

export type ChartToolTipItem = {
  location?: AMLocation
  stat?: ChartToolTipItemStat
  extraStat?: ChartToolTipItemStat
  secondaryStats?: ChartToolTipItemStat[]
}

type BaseChartTooltipProps = {
  titleLabel?: string
  title?: string | number
  children?: React.ReactNode
}

type ChartTooltipProps = {
  titleLabel?: string
  title?: string | number
  items: ChartToolTipItem[]
  color?: string
}

type StatsProps = {
  item: ChartToolTipItem
}

type BasicStatsTextProps = {
  children?: React.ReactNode
  color?: string
}

const BasicStatText: FC<BasicStatsTextProps> = ({ children, color }) => {
  if (!children) {
    return null
  }

  return (
    <Typography variant="text1m" color={color || MOBILITY_LINE_COLORS[0]}>
      <>{children}</>
    </Typography>
  )
}

type StateRowProps = {
  stat: ChartToolTipItemStat
  colorful?: boolean
}
const StatRow: FC<StateRowProps> = ({ stat, colorful }) => {
  const getLegendIcon = useCallback((icon: ChartTooltipLegendIcon, color: string) => {
    switch (icon) {
      case 'shortdot':
        return <ShortDot color={color} />
      case 'dash':
        return <Dash color={color} />
      case 'dashdot':
        return <DashDot color={color} />
      case 'solid':
        return <Solid color={color} />
      case 'square':
        return <Square color={color} size={14} />
      case 'long-dash-dot':
        return <LongDashDot color={color} />
      default:
        return null
    }
  }, [])

  return (
    <ProviderVisitsRow color={colorful ? stat.color : undefined}>
      <ProviderLegendColumn>
        {getLegendIcon(stat.icon, stat.color)}
        <Typography color={Colors.textDark}>{stat.label}</Typography>
      </ProviderLegendColumn>
      <ProviderVisitsColumn>
        <Typography color={colorful ? stat.color : Colors.textDark}>{stat.value}</Typography>
      </ProviderVisitsColumn>
    </ProviderVisitsRow>
  )
}

const Stats: FC<StatsProps> = ({ item }) => {
  const location = item.location

  if (!location) {
    console.warn('ChartTooltip Stats: Location is not defined')
    return null
  }

  const hasSecondaryStats = item.secondaryStats && item.secondaryStats.length > 0
  const hasExtraStat = item.stat && item.extraStat

  return (
    <LocationWrapper>
      <LocationHeaderWrapper>
        <LocationHeader>
          <Typography color={Colors.textDark}>{location.name}</Typography>
          <Typography color={Colors.textMedium}>{fullAddress(location)}</Typography>
        </LocationHeader>
      </LocationHeaderWrapper>
      <LocationContent
        style={
          hasSecondaryStats
            ? {
                minHeight: hasExtraStat ? '104px' : '80px',
                maxHeight: hasExtraStat ? '104px' : '80px',
                paddingBottom: `${Shapes.spacing[1]}px`,
              }
            : {
                paddingBottom: 0,
              }
        }>
        {item.stat && <StatRow stat={item.stat} colorful />}
        {item.extraStat && <StatRow stat={item.extraStat} colorful />}
        {item.secondaryStats?.map((stat, index) => <StatRow key={`${stat.label}${index}`} stat={stat} />)}
      </LocationContent>
    </LocationWrapper>
  )
}

const ChartTooltipColumns: FC<{ items: ChartToolTipItem[] }> = ({ items }) => {
  const columns = useMemo(() => {
    return {
      first: items.slice(0, 4),
      second: items.slice(4, items.length > 8 ? 8 : items.length),
    }
  }, [items])

  return (
    <>
      <LocationsColumn>
        {columns.first.map((item, index) => (
          <Stats key={item.location?.id ?? index} item={item} />
        ))}
      </LocationsColumn>
      <LocationsColumn>
        {columns.second.map((item, index) => (
          <Stats key={item.location?.id ?? `col2-${index}`} item={item} />
        ))}
      </LocationsColumn>
    </>
  )
}

export const BaseChartTooltip: FC<BaseChartTooltipProps> = ({ titleLabel, title, children }) => {
  return (
    <ChartTooltipWrapper>
      <ChartTooltipHeader>
        {titleLabel && <Typography color={Colors.textMedium}>{titleLabel}:</Typography>}
        {title && <Typography color={Colors.textDark}>{title}</Typography>}
      </ChartTooltipHeader>
      <ChartTooltipContent>{children}</ChartTooltipContent>
    </ChartTooltipWrapper>
  )
}

export const ChartTooltip: FC<ChartTooltipProps> = ({ titleLabel, title, items, color }) => {
  if (items.length === 0) {
    return null
  }

  return (
    <BaseChartTooltip titleLabel={titleLabel} title={title}>
      {items.length === 1 && !items[0].secondaryStats ? (
        <BasicStatTexts>
          <BasicStatText color={items[0].stat?.color ?? color}>{items[0].stat?.value}</BasicStatText>
          <BasicStatText color={items[0].extraStat?.color ?? color}>{items[0].extraStat?.value}</BasicStatText>
        </BasicStatTexts>
      ) : (
        <ChartTooltipColumns items={items} />
      )}
    </BaseChartTooltip>
  )
}
